import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Gear, NotePencil, CaretDown, CaretUp } from "@phosphor-icons/react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { api, formatApiErrorDetail } from "../../lib/api";

function QuickNotes({ clientId }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    load();
  }, [clientId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/stores/${clientId}/notes`);
      setNotes(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const addNote = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/stores/${clientId}/notes`, { text });
      setText("");
      await load();
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 border-t border-igc-gold/10 pt-3" data-testid={`quick-notes-${clientId}`}>
      <button type="button" data-testid={`quick-notes-toggle-${clientId}`} onClick={() => setOpen(!open)} title="Shows or hides quick internal talking-point notes about this store — visible only to admins, never to the client." className="flex items-center justify-between w-full text-igc-muted hover:text-igc-goldText transition-colors text-xs uppercase tracking-widest">
        <span className="flex items-center gap-1.5"><NotePencil size={14} weight="duotone" /> Demo Notes{notes && notes.length > 0 ? ` (${notes.length})` : ""}</span>
        {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <div className="space-y-1.5 max-h-28 overflow-y-auto" data-testid={`quick-notes-list-${clientId}`}>
            {loading && <p className="text-igc-muted text-xs">Loading...</p>}
            {!loading && notes && notes.length === 0 && <p className="text-igc-muted text-xs">No notes yet — jot a quick talking point below.</p>}
            {!loading && notes && notes.map((n) => (
              <p key={n.id} className="text-igc-cream text-xs border-l-2 border-igc-gold pl-2" data-testid={`quick-note-${n.id}`}>{n.text}</p>
            ))}
          </div>
          <div className="flex gap-2">
            <Textarea data-testid={`quick-notes-input-${clientId}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a talking point..." title="Type a quick reminder or talking point about this client, then click Add." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream min-h-16 text-xs" />
            <Button data-testid={`quick-notes-add-${clientId}`} onClick={addNote} disabled={submitting} title="Saves this note to the list above." className="bg-igc-gold hover:bg-igc-goldHover text-black text-xs px-3">{submitting ? "..." : "Add"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function StoreOverviewCards({ stores, onEditSettings }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      {stores.map((s, i) => (
        <motion.div key={s.client_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }} className="bg-igc-surface border border-igc-gold/15 p-6" data-testid={`admin-store-card-${s.client_id}`}>
          <div className="flex justify-between items-start mb-3">
            <p className="font-serif text-lg text-igc-cream">{s.display_name}</p>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] px-2 py-1 tracking-wide ${s.status === "active" ? "bg-igc-success/10 text-igc-success" : "bg-igc-gold/10 text-igc-goldText"}`} title="Whether this client's store is currently live/active in the system or in another state (e.g. onboarding).">{s.status}</span>
              <button data-testid={`edit-store-settings-${s.client_id}`} onClick={() => onEditSettings(s)} title="Opens Store Settings — control which modules this client can access, ACH payment status, revenue projections, and internal notes." className="text-igc-muted hover:text-igc-goldText transition-colors">
                <Gear size={16} weight="duotone" />
              </button>
            </div>
          </div>
          <p className="text-igc-muted text-xs mb-1">Client ID</p>
          <p className="text-igc-cream text-sm mb-4">{s.client_id}</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-igc-muted text-xs" title="Total revenue this client's store has generated so far.">Revenue</p><p className="text-igc-cream" data-testid={`admin-store-revenue-${s.client_id}`}>${s.total_revenue.toLocaleString()}</p></div>
            <div><p className="text-igc-muted text-xs" title="What percentage of this client's upfront investment has been recovered through store revenue so far.">ROI Recovered</p><p className="text-igc-goldText" data-testid={`admin-store-roi-${s.client_id}`}>{s.roi_recovered_pct}%</p></div>
          </div>
          <QuickNotes clientId={s.client_id} />
        </motion.div>
      ))}
    </div>
  );
}
